import { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";
import { MdEmail, MdPeople, MdHistory, MdQrCode2, MdFamilyRestroom } from "react-icons/md";
import { BsFillTelephoneFill, BsSearch } from "react-icons/bs";
import { RiArrowDownSFill } from "react-icons/ri";
import { RxCross1 } from "react-icons/rx";
import { format } from "date-fns";
import QRCode from "react-qr-code";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import useGroup from "../../hook/useGroup";
import Pagination from "../../components/Pagination";
import { getVisitorHistories, getVisitorPaging, getVisitorSearchPaging, post_ConvertVisitorsToFamily } from "../../services/Visitor";
import { formatUSPhone } from "../../utils/phoneFormatter";
import { getGroupById } from "../../services/Group";
import useConfirm from "../../hook/useConfirm";

type FeelingOption = {
    value: number;
    emoji: string;
    label: string;
};

const feelingOptions: FeelingOption[] = [
    {
        value: 1,
        emoji: "😔",
        label: "Life is Bad"
    },
    {
        value: 2,
        emoji: "💔",
        label: "I'm Hurting"
    },
    {
        value: 3,
        emoji: "😐",
        label: "It Is What It Is"
    },
    {
        value: 4,
        emoji: "🙂",
        label: "I'm Good"
    },
    {
        value: 5,
        emoji: "😊",
        label: "Life is Great"
    }
];

const getWellbeing = (rating: any) => {
    if (
        rating === null ||
        rating === undefined ||
        rating === ""
    ) {
        return null;
    }

    const numericRating = Number(rating);

    return (
        feelingOptions.find((item) => item.value === numericRating) || null
    );
};

const Visitor = () => {
    const token = Cookies.get("token-my-cdir") || "";

    const { selectedGroupId }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const [visitors, setVisitors] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        size: 10
    });

    const [showEntries, setShowEntries] = useState<number>(10);

    const [totalLength, setTotalLength] = useState<number>(0);

    const [searchValue, setSearchValue] = useState<string>("");

    const [searchType, setSearchType] = useState<string>("name");

    const [displayFilter, setDisplayFilter] = useState<boolean>(false);

    const [selectedFilter, setSelectedFilter] = useState<string>("Visitor Name");

    const [selectedVisitors, setSelectedVisitors] = useState<number[]>([]);

    const [showQR, setShowQR] = useState<boolean>(false);

    const [visitorLink, setVisitorLink] = useState<string>("");

    const [showHistory, setShowHistory] = useState<boolean>(false);

    const [historyVisitor, setHistoryVisitor] = useState<any>(null);

    const [historyData, setHistoryData] = useState<any[]>([]);

    const [historyLoading, setHistoryLoading] = useState<boolean>(false);

    const [historyPagination, setHistoryPagination] = useState({
        page: 0,
        size: 10
    });

    const [historyTotal, setHistoryTotal] = useState<number>(0);

    const [isConverting, setIsConverting] = useState<boolean>(false);

    const [expandedVisitorId, setExpandedVisitorId] = useState<number | null>(null);

    const qrRef = useRef<HTMLDivElement | null>(null);

    const loadVisitors = async (
        page = paginationModel.page,
        size = paginationModel.size,
        currentSearchValue = searchValue,
        currentSearchType = searchType
    ) => {
        if (!selectedGroupId || selectedGroupId <= 0) {
            setVisitors([]);
            setTotalLength(0);
            return;
        }

        try {
            setIsLoading(true);

            let data;

            if (currentSearchValue.trim()) {
                data = await getVisitorSearchPaging(
                    selectedGroupId,
                    currentSearchValue.trim(),
                    currentSearchType,
                    page,
                    size,
                    token
                );
            } else {
                data = await getVisitorPaging(
                    selectedGroupId,
                    page,
                    size,
                    token
                );
            }

            const result = (data?.visitors || [])
                .map((item: any) => ({
                    ...item,
                    name: `${item?.firstName || ""} ${item?.lastName || ""}`.trim(),
                    formattedDate: item?.createdAt
                        ? format(
                            new Date(item.createdAt),
                            "MM/dd/yyyy"
                        )
                        : "No data"
                }));

            setVisitors(result);
            setTotalLength(data?.count || 0);

            setSelectedVisitors((prev) =>
                prev.filter((id) =>
                    result.some(
                        (item: any) =>
                            item.id === id &&
                            !item.isConvertedToFamily
                    )
                )
            );
        } catch (error) {
            console.error("Visitor loading error:", error);

            setVisitors([]);
            setTotalLength(0);
            setSelectedVisitors([]);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!selectedGroupId || selectedGroupId <= 0) {
            setVisitors([]);
            setTotalLength(0);
            return;
        }

        const timeout = setTimeout(() => {
            loadVisitors(
                paginationModel.page,
                paginationModel.size,
                searchValue,
                searchType
            );
        }, searchValue.trim() ? 700 : 0);

        return () => clearTimeout(timeout);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedGroupId,
        paginationModel.page,
        paginationModel.size,
        searchValue,
        searchType
    ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setPaginationModel((prev) => {
                if (prev.page !== 0) {
                    return {
                        ...prev,
                        page: 0
                    };
                }

                return prev;
            });
        }, 700);

        return () => clearTimeout(timeout);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, searchType]);

    useEffect(() => {
        setSearchValue("");
        setSearchType("name");
        setSelectedVisitors([]);
        setExpandedVisitorId(null);
        setHistoryVisitor(null);
        setHistoryData([]);
        setHistoryTotal(0);
        setHistoryPagination({
            page: 0,
            size: 10
        });
        setPaginationModel({
            page: 0,
            size: 10
        });
        setShowEntries(10);
    }, [selectedGroupId]);

    const toggleVisitor = (visitor: any) => {
        if (
            !visitor ||
            visitor.isConvertedToFamily ||
            !visitor.id
        ) {
            return;
        }

        const id = visitor.id;

        setSelectedVisitors((prev) =>
            prev.includes(id)
                ? prev.filter((item) => item !== id)
                : [...prev, id]
        );
    };

    const convertibleVisitors = visitors.filter(
        (item) =>
            !item.isConvertedToFamily &&
            item.id !== null &&
            item.id !== undefined
    );

    const convertibleVisitorIds = convertibleVisitors.map((item) => item.id);

    const allConvertibleSelected =
        convertibleVisitorIds.length > 0 &&
        convertibleVisitorIds.every((id) =>
            selectedVisitors.includes(id)
        );

    const toggleAll = () => {
        if (convertibleVisitorIds.length === 0) {
            setSelectedVisitors([]);
            return;
        }

        if (allConvertibleSelected) {
            setSelectedVisitors((prev) =>
                prev.filter(
                    (id) =>
                        !convertibleVisitorIds.includes(
                            id
                        )
                )
            );
        } else {
            setSelectedVisitors((prev) =>
                Array.from(
                    new Set([
                        ...prev,
                        ...convertibleVisitorIds
                    ])
                )
            );
        }
    }

    const applyFilter = () => {
        const filterMap: any = {
            name: "Visitor Name",
            email: "Email",
            phone: "Phone",
            firstname: "First Name",
            lastname: "Last Name"
        };

        setSelectedFilter(
            filterMap[searchType] ||
            "Visitor Name"
        );

        setDisplayFilter(false);

        setPaginationModel((prev) => ({
            ...prev,
            page: 0
        }));
    }

    const clearFilter = () => {
        setSearchValue("");
        setSearchType("name");
        setSelectedFilter("Visitor Name");
        setDisplayFilter(false);

        setPaginationModel((prev) => ({
            ...prev,
            page: 0
        }));
    }

    const generateQR = async () => {
        try {
            const response =
                await getGroupById(
                    selectedGroupId,
                    token
                );

            const encryptedGroupId =
                response?.groupId;

            if (!encryptedGroupId) {
                setConfirmData({
                    title: "Warning",
                    summary: "Encrypted Group ID is not available.",
                    confirmBtn: "Ok",
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);
                return;
            }

            const link = `${window.location.origin}/public/register/${encodeURIComponent(encryptedGroupId)}`;

            setVisitorLink(link);
            setShowQR(true);
        } catch (error) {
            console.error(
                "Failed to generate QR:",
                error
            );

            setConfirmData({
                title: "Warning",
                summary: "Unable to generate QR code.",
                confirmBtn: "Ok",
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);
        }
    }

    const downloadQRAsPNG = async () => {
        if (!qrRef.current) return;

        try {
            const dataUrl = await toPng(
                qrRef.current,
                {
                    backgroundColor: "#ffffff",
                    pixelRatio: 3
                }
            );

            const link = document.createElement("a");

            link.download = "visitor-registration-qr.png";

            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error(
                "Failed to download PNG:",
                error
            );
        }
    };

    const downloadQRAsPDF = async () => {
        if (!qrRef.current) return;

        try {
            const dataUrl = await toPng(
                qrRef.current,
                {
                    backgroundColor: "#ffffff",
                    pixelRatio: 3
                }
            );

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const pageWidth = pdf.internal.pageSize.getWidth();

            const imgSize = 80;

            pdf.setFontSize(18);

            pdf.text(
                "Visitor Registration",
                pageWidth / 2,
                30,
                {
                    align: "center"
                }
            );

            pdf.addImage(
                dataUrl,
                "PNG",
                (pageWidth - imgSize) / 2,
                45,
                imgSize,
                imgSize
            );

            pdf.setFontSize(10);

            pdf.text(
                "Scan this QR code to register as a visitor.",
                pageWidth / 2,
                135,
                {
                    align: "center"
                }
            );

            pdf.setFontSize(8);

            pdf.text(
                visitorLink,
                pageWidth / 2,
                145,
                {
                    align: "center",
                    maxWidth: 170
                }
            );

            pdf.save("visitor-registration-qr.pdf");
        } catch (error) {
            console.error(
                "Failed to download PDF:",
                error
            );
        }
    };

    const printQR = () => {
        const qrContainer = qrRef.current;

        if (!qrContainer) {
            console.error("QR container not found");
            return;
        }

        const qrSvg = qrContainer.querySelector("svg") as SVGElement | null;

        if (!qrSvg) {
            console.error("QR SVG not found");
            return;
        }

        const svgString = new XMLSerializer().serializeToString(qrSvg);

        const printWindow = window.open("", "_blank");

        if (!printWindow) {
            setConfirmData({
                title: "Warning",
                summary: "Please allow popups to print",
                confirmBtn: "Ok",
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);
            return;
        }

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <title>Visitor Registration QR</title>

                <style>
                    @page {
                        size: A4;
                        margin: 20mm;
                    }

                    body {
                        margin: 0;
                        padding: 40px;
                        font-family: Arial, sans-serif;
                        text-align: center;
                        color: #111;
                    }

                    h1 {
                        margin-bottom: 10px;
                    }

                    p {
                        color: #555;
                        font-size: 14px;
                    }

                    .qr {
                        margin: 30px auto;
                    }

                    .qr svg {
                        width: 300px;
                        height: 300px;
                    }

                    .link {
                        max-width: 600px;
                        margin: 20px auto;
                        padding: 12px;
                        border: 1px solid #ddd;
                        border-radius: 6px;
                        font-size: 12px;
                        word-break: break-all;
                        color: #555;
                    }
                </style>
            </head>

            <body>
                <h1>Visitor Registration</h1>

                <p>
                    Scan this QR code to register as a visitor.
                </p>

                <div class="qr">
                    ${svgString}
                </div>

                <div class="link">
                    ${visitorLink}
                </div>
            </body>
        </html>
        `);

        printWindow.document.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 500);
    }

    const copyVisitorLink =
        async () => {
            try {
                await navigator.clipboard.writeText(
                    visitorLink
                );

                setConfirmData({
                    title: "Warning",
                    summary:
                        "Visitor link copied.",
                    confirmBtn: "Ok",
                    isConfirm: (
                        callback: any
                    ) => {
                        callback();
                    }
                });

                setConfirmPopup(true);
            } catch {
                setConfirmData({
                    title: "Warning",
                    summary: "Unable to copy link.",
                    confirmBtn: "Ok",
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);
            }
        }

    const loadHistory = async (
        visitorId: number,
        page: number,
        size: number
    ) => {
        if (
            !selectedGroupId ||
            selectedGroupId <= 0
        ) {
            setHistoryData([]);
            setHistoryTotal(0);
            return;
        }

        try {
            setHistoryLoading(true);

            const data =
                await getVisitorHistories(
                    visitorId,
                    selectedGroupId,
                    page,
                    size,
                    token
                );

            setHistoryData(data?.visitors || []);

            setHistoryTotal(data?.count || 0);

            setHistoryPagination({
                page,
                size
            });
        } catch (error) {
            console.error(
                "History loading error:",
                error
            );

            setHistoryData([]);
            setHistoryTotal(0);
        } finally {
            setHistoryLoading(false);
        }
    }

    const toggleVisitorHistory =
        async (visitor: any) => {
            if (expandedVisitorId === visitor.id) {
                setExpandedVisitorId(null);
                return;
            }

            setExpandedVisitorId(visitor.id);

            setHistoryVisitor(visitor);

            setHistoryPagination({
                page: 0,
                size: historyPagination.size
            });

            await loadHistory(
                visitor.id,
                0,
                historyPagination.size
            );
        };

    const convertVisitors = async (
        visitorIds: number[],
        _?: (callback: any) => void
    ) => {
        const validVisitorIds = visitorIds.filter((id) => {
            const visitor = visitors.find((item) => item.id === id);

            return visitor && !visitor.isConvertedToFamily;
        });

        if (validVisitorIds.length === 0) {
            setSelectedVisitors([]);

            setConfirmData({
                title: "Warning",
                summary: "Please select at least one visitor that has not already been converted.",
                confirmBtn: "Ok",
                isConfirm: (callback: any) => {
                    callback();
                }
            });

            setConfirmPopup(true);

            return;
        }

        setConfirmData({
            title: "Alert",
            summary: `Convert ${validVisitorIds.length} visitor(s) to member?`,
            confirmBtn: "Yes",
            isCancelBtn: true,
            isConfirm: async (cb: any) => {
                try {
                    setIsConverting(true);

                    const result =
                        await post_ConvertVisitorsToFamily(
                            selectedGroupId,
                            validVisitorIds,
                            token
                        );

                    setSelectedVisitors([]);

                    await loadVisitors(
                        paginationModel.page,
                        paginationModel.size,
                        searchValue,
                        searchType
                    );

                    cb();

                    setTimeout(() => {
                        const convertedCount = result?.convertedCount || 0;
                        const skippedCount = result?.skippedCount || 0;

                        let summary = "";

                        if (convertedCount > 0) {
                            summary += `Converted: ${convertedCount}`;
                        }

                        if (skippedCount > 0) {
                            if (summary) {
                                summary += "\n\n";
                            }

                            summary +=
                                skippedCount === 1
                                    ? "This visitor was not converted because a member with the same email already exists."
                                    : `${skippedCount} visitors were not converted because members with the same email already exist.`;
                        }

                        setConfirmData({
                            title: "Alert",
                            summary,
                            confirmBtn: "Ok",
                            isConfirm: (callback: any) => {
                                callback();
                            }
                        });

                        setConfirmPopup(true);
                    }, 100);
                } catch (error: any) {
                    console.error(
                        "Convert visitor error:",
                        error
                    );

                    cb();

                    setTimeout(() => {
                        setConfirmData({
                            title: "Warning",
                            summary: error?.response?.data || "Unable to convert visitors.",
                            confirmBtn: "Ok",
                            isConfirm: (callback: any) => {
                                callback();
                            }
                        });

                        setConfirmPopup(true);
                    }, 100);
                } finally {
                    setIsConverting(false);
                }
            }
        });

        setConfirmPopup(true);
    }

    const convertToFamily =
        async () => {
            await convertVisitors(selectedVisitors);
        };

    return (
        <section className="user__container p-3 xm:p-4 overflow-x-hidden">
            <div className="min-w-full flex flex-col xm:flex-row justify-between items-start xm:items-center gap-3 text-xs xm:text-sm md:text-base">

                <h1 className="px-2 xm:px-4 py-1.5 flex items-center gap-2">
                    <MdPeople fontSize={22} />

                    <span className="xm:text-sm md:text-base">
                        Visitors
                    </span>
                </h1>

                <div className="flex flex-col xm:flex-row gap-2 w-full xm:w-auto">
                    <button
                        type="button"
                        onClick={generateQR}
                        className="bg-secondary hover:bg-custom-blue px-4 py-2 rounded-sm text-white flex items-center justify-center gap-2"
                    >
                        <MdQrCode2 size={18} />

                        Generate Visitor QR
                    </button>

                    <button
                        type="button"
                        disabled={selectedVisitors.length === 0 || isConverting}
                        onClick={convertToFamily}
                        className={`px-4 py-2 rounded-sm text-white flex items-center justify-center gap-2 ${selectedVisitors.length ===
                            0 ||
                            isConverting
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-secondary hover:bg-custom-blue"
                            }`}
                    >
                        <MdFamilyRestroom size={18} />

                        {isConverting
                            ? "Converting..."
                            : `Convert to Member ${selectedVisitors.length
                                ? `(${selectedVisitors.length})`
                                : ""
                            }`}
                    </button>
                </div>
            </div>

            <div className="flex flex-col xm:flex-row gap-3 justify-between xm:items-center border border-black p-2 mt-4">
                <div className="relative flex flex-col xm:flex-row gap-3 items-start xm:items-center w-full">
                    <div className="relative w-full xm:w-auto">
                        <button
                            type="button"
                            className="bg-secondary text-white px-3 py-2 rounded-md flex items-center justify-between w-full xm:w-auto whitespace-nowrap"
                            onClick={() => setDisplayFilter(!displayFilter)}
                        >
                            {selectedFilter}

                            <RiArrowDownSFill className="ml-1" />
                        </button>

                        {displayFilter && (
                            <div className="absolute z-20 w-full xm:w-44 p-2 bg-white border border-black mt-2 xm:mt-0 xm:top-0 xm:left-full">
                                <div className="flex justify-between">
                                    <label className="text-xs font-bold">
                                        Fields
                                    </label>

                                    <div
                                        onClick={() => setDisplayFilter(false)}
                                        className="w-4 h-4 cursor-pointer"
                                    >
                                        <RxCross1 />
                                    </div>
                                </div>

                                <select
                                    className="w-full text-xs py-1 border mt-1"
                                    value={
                                        searchType
                                    }
                                    onChange={(e) => setSearchType(e.target.value)}
                                >
                                    <option value="name">
                                        Visitor Name
                                    </option>

                                    <option value="email">
                                        Email
                                    </option>

                                    <option value="phone">
                                        Phone
                                    </option>

                                    <option value="firstname">
                                        First Name
                                    </option>

                                    <option value="lastname">
                                        Last Name
                                    </option>
                                </select>

                                <div className="flex mt-2 gap-1">
                                    <button
                                        type="button"
                                        onClick={applyFilter}
                                        className="text-xs bg-secondary text-white py-1 px-2 rounded-md w-full"
                                    >
                                        Apply
                                    </button>

                                    <button
                                        type="button"
                                        onClick={clearFilter}
                                        className="text-xs bg-secondary text-white py-1 px-2 rounded-md w-full"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2 w-full border px-2 py-1 rounded xm:border-none">
                        <div className="h-4 w-4 text-gray-900">
                            <BsSearch />
                        </div>

                        <input
                            type="text"
                            placeholder="Search visitors..."
                            className="w-full text-sm xm:text-lg pl-2 outline-none"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {visitors.length >
                0 && (
                    <div className="flex items-center gap-2 mt-4 px-2">
                        <input
                            type="checkbox"
                            checked={allConvertibleSelected}
                            disabled={convertibleVisitorIds.length === 0}
                            onChange={toggleAll}
                            className="w-4 h-4 accent-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        />

                        <span className="text-sm">
                            Select All
                        </span>

                        {selectedVisitors.length >
                            0 && (
                                <span className="text-sm text-secondary font-semibold">
                                    {
                                        selectedVisitors.length
                                    }{" "}
                                    selected
                                </span>
                            )}
                    </div>
                )}

            {!isLoading ? (
                <>
                    {visitors.length >
                        0 ? (
                        <div className="mt-4 w-full">
                            <div className="hidden md:block bg-white border border-gray-200 shadow-sm overflow-hidden">
                                <div className="grid grid-cols-[50px_1.5fr_1.5fr_1fr_1fr_1.2fr_150px] items-center bg-gray-200 border-b px-5 py-3 text-xs lg:text-sm font-semibold text-black tracking-wide">
                                    <div>
                                        <input
                                            type="checkbox"
                                            checked={allConvertibleSelected}
                                            disabled={convertibleVisitorIds.length === 0}
                                            onChange={toggleAll}
                                            className="w-4 h-4 accent-secondary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                    </div>

                                    <div>
                                        Visitor
                                    </div>

                                    <div>
                                        Contact
                                    </div>

                                    <div>
                                        Wellbeing
                                    </div>

                                    <div>
                                        Last Visit
                                    </div>

                                    <div>
                                        Status
                                    </div>

                                    <div className="text-center">
                                        Actions
                                    </div>
                                </div>

                                {visitors.map((item, index) => {
                                    const wellbeing = getWellbeing(item.wellbeingRating);

                                    const isExpanded = expandedVisitorId === item.id;

                                    return (
                                        <div
                                            key={item.id || index}
                                        >
                                            <div
                                                className={`
                                                        grid
                                                        grid-cols-[50px_1.5fr_1.5fr_1fr_1fr_1.2fr_150px]
                                                        items-center
                                                        px-5
                                                        py-4
                                                        border-b
                                                        transition-colors
                                                        ${isExpanded
                                                        ? "bg-secondary/5"
                                                        : "hover:bg-gray-50"
                                                    }
                                                    `}
                                            >
                                                <div>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedVisitors.includes(item.id)}
                                                        disabled={item.isConvertedToFamily}
                                                        onChange={() => toggleVisitor(item)}
                                                        className={`w-4 h-4 accent-secondary ${item.isConvertedToFamily
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : "cursor-pointer"
                                                            }`}
                                                    />
                                                </div>

                                                <div className="min-w-0 pr-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-secondary/10 text-secondary flex items-center justify-center font-bold text-sm shrink-0">
                                                            {(
                                                                item.firstName?.[0] ||
                                                                item.name?.[0] ||
                                                                "V"
                                                            ).toUpperCase()}
                                                        </div>

                                                        <div className="min-w-0">
                                                            <p className="font-semibold text-sm text-gray-900 truncate">
                                                                {item.name || "Unknown Visitor"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="min-w-0 pr-4 space-y-1">
                                                    {item.email ? (
                                                        <div className="flex items-center gap-2 text-xs text-gray-700">
                                                            <MdEmail
                                                                size={14}
                                                                className="text-gray-400 shrink-0"
                                                            />

                                                            <span className="truncate">
                                                                {item.email}
                                                            </span>
                                                        </div>
                                                    ) : null}

                                                    {item.phoneNumber ? (
                                                        <div className="flex items-center gap-2 text-xs text-gray-600">
                                                            <BsFillTelephoneFill
                                                                size={11}
                                                                className="text-gray-400 shrink-0"
                                                            />

                                                            <span>
                                                                {formatUSPhone(item.phoneNumber)}
                                                            </span>
                                                        </div>
                                                    ) : null}

                                                    {!item.email &&
                                                        !item.phoneNumber && (
                                                            <span className="text-xs text-gray-400">
                                                                No contact information
                                                            </span>
                                                        )}
                                                </div>

                                                <div>
                                                    {wellbeing ? (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-9 h-9 rounded-full bg-gray-50 border flex items-center justify-center text-xl">
                                                                {wellbeing.emoji}
                                                            </div>

                                                            <div className="min-w-0">
                                                                <p className="text-xs font-bold text-gray-800">
                                                                    {wellbeing.label}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">
                                                            Not provided
                                                        </span>
                                                    )}
                                                </div>

                                                <div>
                                                    <p className="text-sm font-medium text-gray-700">
                                                        {item.formattedDate}
                                                    </p>

                                                    <p className="text-xs text-gray-400 mt-0.5">
                                                        Last visit
                                                    </p>
                                                </div>

                                                <div>
                                                    {item.isConvertedToFamily ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                            Member
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-700 text-xs font-semibold">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                                                            Visitor
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        type="button"
                                                        title="View history"
                                                        onClick={() => toggleVisitorHistory(item)}
                                                        className={`
                                                                w-8 h-8
                                                                rounded-md
                                                                flex
                                                                items-center
                                                                justify-center
                                                                transition
                                                                ${isExpanded
                                                                ? "bg-secondary text-white"
                                                                : "bg-gray-100 text-gray-600 hover:bg-secondary hover:text-white"
                                                            }
                                                            `}
                                                    >
                                                        <RiArrowDownSFill
                                                            size={18}
                                                            className={`
                                                                    transition-transform
                                                                    ${isExpanded
                                                                    ? "rotate-180"
                                                                    : ""
                                                                }
                                                                `}
                                                        />
                                                    </button>

                                                    {!item.isConvertedToFamily && (
                                                        <button
                                                            type="button"
                                                            title="Convert to member"
                                                            disabled={isConverting}
                                                            onClick={() => convertVisitors([item.id])}
                                                            className={`w-8 h-8 rounded-md bg-secondary/10 text-secondary hover:bg-secondary hover:text-white flex items-center justify-center transition ${isConverting
                                                                ? "opacity-50 cursor-not-allowed"
                                                                : ""
                                                                }`}
                                                        >
                                                            <MdFamilyRestroom
                                                                size={17}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="border-b bg-gray-50">
                                                    <div className="px-6 lg:px-12 py-5">
                                                        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-4">
                                                            <div>
                                                                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                                                    <MdHistory
                                                                        size={18}
                                                                        className="text-secondary"
                                                                    />
                                                                    Visitor History
                                                                </h3>

                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    Previous visits for{" "}
                                                                    <span className="font-semibold text-gray-600">
                                                                        {item.name}
                                                                    </span>
                                                                </p>
                                                            </div>

                                                            <span className="text-xs text-gray-400">
                                                                {
                                                                    historyTotal
                                                                }{" "}
                                                                visit
                                                                {historyTotal !==
                                                                    1
                                                                    ? "s"
                                                                    : ""}
                                                            </span>
                                                        </div>

                                                        {historyLoading ? (
                                                            <div className="h-28 flex flex-col items-center justify-center">
                                                                <div className="h-7 w-7 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary" />

                                                                <span className="text-xs text-gray-500 mt-2">
                                                                    Loading history...
                                                                </span>
                                                            </div>
                                                        ) : historyData.length >
                                                            0 ? (
                                                            <div className="space-y-3">
                                                                {historyData.map((history, historyIndex) => {
                                                                    const historyFeeling = getWellbeing(history.wellbeingRating);

                                                                    return (
                                                                        <div
                                                                            key={history.id || historyIndex}
                                                                            className="bg-white border border-gray-200 rounded-lg p-4"
                                                                        >
                                                                            <div className="flex flex-col sm:flex-row justify-between gap-4">
                                                                                <div>
                                                                                    <p className="text-xs text-gray-400 mt-1">
                                                                                        {history.createdAt
                                                                                            ? format(
                                                                                                new Date(
                                                                                                    history.createdAt
                                                                                                ),
                                                                                                "MM/dd/yyyy hh:mm a"
                                                                                            )
                                                                                            : "No date"}
                                                                                    </p>
                                                                                </div>

                                                                                {historyFeeling && (
                                                                                    <div className="flex items-center gap-2">
                                                                                        <div className="text-2xl">
                                                                                            {historyFeeling.emoji}
                                                                                        </div>

                                                                                        <div>
                                                                                            <p className="text-xs text-gray-400">
                                                                                                Wellbeing
                                                                                            </p>

                                                                                            <p className="text-xs font-bold text-gray-700">
                                                                                                {historyFeeling.label}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            {history.prayerRequest && (
                                                                                <div className="mt-4 pt-3 border-t">
                                                                                    <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">
                                                                                        Prayer Request
                                                                                    </p>

                                                                                    <p className="text-sm text-gray-700 mt-1 break-words">
                                                                                        {history.prayerRequest}
                                                                                    </p>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="bg-white border border-dashed border-gray-300 rounded-lg h-24 flex items-center justify-center">
                                                                <p className="text-sm text-gray-400">
                                                                    No visitor history found.
                                                                </p>
                                                            </div>
                                                        )}

                                                        {historyTotal >
                                                            0 && (
                                                                <div className="mt-4 pt-4 border-t">
                                                                    <Pagination
                                                                        pageOption={[
                                                                            5,
                                                                            10,
                                                                            15,
                                                                            20
                                                                        ]}
                                                                        totalLength={historyTotal}
                                                                        paginationModel={historyPagination}
                                                                        setPaginationModel={(model: any) => {
                                                                            setHistoryPagination(model);

                                                                            loadHistory(
                                                                                item.id,
                                                                                model.page,
                                                                                model.size
                                                                            );
                                                                        }}
                                                                        showEntries={historyPagination.size}
                                                                        setShowEntries={(size: number) => {
                                                                            setHistoryPagination({
                                                                                page: 0,
                                                                                size
                                                                            });

                                                                            loadHistory(
                                                                                item.id,
                                                                                0,
                                                                                size
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                )}
                            </div>

                            <div className="md:hidden space-y-3">
                                {visitors.map((item, index) => {
                                    const wellbeing = getWellbeing(item.wellbeingRating);

                                    const isExpanded = expandedVisitorId === item.id;

                                    return (
                                        <div
                                            key={item.id || index}
                                            className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
                                        >
                                            <div className="p-4">
                                                <div className="flex items-start gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedVisitors.includes(item.id)}
                                                        disabled={item.isConvertedToFamily}
                                                        onChange={() => toggleVisitor(item)}
                                                        className={`w-4 h-4 mt-1 accent-secondary shrink-0 ${item.isConvertedToFamily
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : "cursor-pointer"
                                                            }`}
                                                    />

                                                    <div className="w-11 h-11 rounded-full bg-secondary/10 text-secondary flex items-center justify-center font-bold shrink-0">
                                                        {(
                                                            item.firstName?.[0] ||
                                                            item.name?.[0] ||
                                                            "V"
                                                        ).toUpperCase()}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-start gap-2">
                                                            <div className="min-w-0">
                                                                <h2 className="font-bold text-base truncate">
                                                                    {item.name || "Unknown Visitor"}
                                                                </h2>

                                                                <p className="text-xs text-gray-400 mt-0.5">
                                                                    Visitor #
                                                                    {item.id}
                                                                </p>
                                                            </div>

                                                            {item.isConvertedToFamily ? (
                                                                <span className="shrink-0 bg-green-50 text-green-700 px-2 py-1 rounded-full text-[10px] font-semibold">
                                                                    Member
                                                                </span>
                                                            ) : (
                                                                <span className="shrink-0 bg-yellow-50 text-yellow-700 px-2 py-1 rounded-full text-[10px] font-semibold">
                                                                    Visitor
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-4 space-y-2">
                                                    {item.email && (
                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                            <MdEmail
                                                                size={15}
                                                                className="text-gray-400"
                                                            />

                                                            <span className="break-all">
                                                                {item.email}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {item.phoneNumber && (
                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                            <BsFillTelephoneFill
                                                                size={12}
                                                                className="text-gray-400"
                                                            />

                                                            {formatUSPhone(item.phoneNumber)}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mt-4 pt-3 border-t">
                                                    <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">
                                                        Wellbeing
                                                    </p>

                                                    {wellbeing ? (
                                                        <div className="flex items-center gap-3 mt-2">
                                                            <div className="w-11 h-11 rounded-full bg-gray-50 border flex items-center justify-center text-2xl">
                                                                {wellbeing.emoji}
                                                            </div>

                                                            <div>
                                                                <p className="font-bold text-sm text-gray-800">
                                                                    {wellbeing.label}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 mt-2">
                                                            Not provided
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="mt-3">
                                                    <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">
                                                        Last Visit
                                                    </p>

                                                    <p className="text-sm font-semibold text-gray-700 mt-1">
                                                        {item.formattedDate}
                                                    </p>
                                                </div>

                                                {item.prayerRequest && (
                                                    <div className="mt-3 bg-gray-50 rounded-lg p-3">
                                                        <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">
                                                            Prayer Request
                                                        </p>

                                                        <p className="text-sm text-gray-700 mt-1 break-words">
                                                            {item.prayerRequest}
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-2 gap-2 mt-4">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleVisitorHistory(item)}
                                                        className={`
                                                                py-2.5
                                                                rounded-md
                                                                text-xs
                                                                font-bold
                                                                flex
                                                                items-center
                                                                justify-center
                                                                gap-2
                                                                transition
                                                                ${isExpanded
                                                                ? "bg-secondary text-white"
                                                                : "bg-gray-100 text-gray-700 hover:bg-secondary hover:text-white"
                                                            }
                                                            `}
                                                    >
                                                        <MdHistory
                                                            size={16}
                                                        />

                                                        {isExpanded
                                                            ? "Hide History"
                                                            : "View History"}
                                                    </button>

                                                    {!item.isConvertedToFamily && (
                                                        <button
                                                            type="button"
                                                            disabled={isConverting}
                                                            onClick={() => convertVisitors([item.id])}
                                                            className={`py-2.5 rounded-md border border-secondary text-secondary hover:bg-secondary hover:text-white text-xs font-bold flex items-center justify-center gap-2 ${isConverting
                                                                ? "opacity-50 cursor-not-allowed"
                                                                : ""
                                                                }`}
                                                        >
                                                            <MdFamilyRestroom
                                                                size={16}
                                                            />

                                                            Convert
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="border-t bg-gray-50 p-4">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <MdHistory
                                                            size={18}
                                                            className="text-secondary"
                                                        />

                                                        <h3 className="font-bold text-sm">
                                                            Visitor History
                                                        </h3>
                                                    </div>

                                                    {historyLoading ? (
                                                        <div className="h-24 flex flex-col items-center justify-center">
                                                            <div className="h-6 w-6 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary" />

                                                            <span className="text-xs text-gray-500 mt-2">
                                                                Loading history...
                                                            </span>
                                                        </div>
                                                    ) : historyData.length >
                                                        0 ? (
                                                        <div className="space-y-3">
                                                            {historyData.map((history, historyIndex) => {
                                                                const historyFeeling = getWellbeing(history.wellbeingRating);

                                                                return (
                                                                    <div
                                                                        key={history.id || historyIndex}
                                                                        className="bg-white border rounded-lg p-3"
                                                                    >
                                                                        <div className="flex justify-between gap-3">
                                                                            <div>
                                                                                <p className="text-xs text-gray-400 mt-1">
                                                                                    {history.createdAt
                                                                                        ? format(
                                                                                            new Date(
                                                                                                history.createdAt
                                                                                            ),
                                                                                            "MM/dd/yyyy hh:mm a"
                                                                                        )
                                                                                        : "No date"}
                                                                                </p>
                                                                            </div>

                                                                            {historyFeeling && (
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-xl">
                                                                                        {historyFeeling.emoji}
                                                                                    </span>

                                                                                    <span className="text-[10px] font-semibold text-gray-600">
                                                                                        {historyFeeling.label}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {history.prayerRequest && (
                                                                            <div className="mt-3 pt-3 border-t">
                                                                                <p className="text-[10px] uppercase font-bold text-gray-400">
                                                                                    Prayer Request
                                                                                </p>

                                                                                <p className="text-sm text-gray-700 mt-1">
                                                                                    {history.prayerRequest}
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="bg-white border border-dashed rounded-lg p-6 text-center">
                                                            <p className="text-xs text-gray-400">
                                                                No visitor history found.
                                                            </p>
                                                        </div>
                                                    )}

                                                    {historyTotal >
                                                        0 && (
                                                            <div className="mt-4 pt-3 border-t">
                                                                <Pagination
                                                                    pageOption={[
                                                                        5,
                                                                        10,
                                                                        15,
                                                                        20
                                                                    ]}
                                                                    totalLength={historyTotal}
                                                                    paginationModel={historyPagination}
                                                                    setPaginationModel={(model: any) => {
                                                                        setHistoryPagination(model);

                                                                        loadHistory(
                                                                            item.id,
                                                                            model.page,
                                                                            model.size
                                                                        );
                                                                    }}
                                                                    showEntries={historyPagination.size}
                                                                    setShowEntries={(size: number) => {
                                                                        setHistoryPagination({
                                                                            page: 0,
                                                                            size
                                                                        });

                                                                        loadHistory(
                                                                            item.id,
                                                                            0,
                                                                            size
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="min-w-full h-[250px] flex flex-col justify-center items-center text-center">
                            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                <MdPeople
                                    size={28}
                                    className="text-gray-400"
                                />
                            </div>

                            <span className="text-sm font-semibold text-gray-600">
                                No Visitors Found
                            </span>

                            <span className="text-xs text-gray-400 mt-1">
                                Try changing your search or filter.
                            </span>
                        </div>
                    )}

                    {totalLength >
                        0 && (
                            <div className="mt-6 flex flex-col xm:flex-row items-center justify-between gap-3">
                                <Pagination
                                    pageOption={[
                                        5,
                                        10,
                                        15,
                                        20,
                                        25,
                                        50,
                                        75,
                                        100
                                    ]}
                                    totalLength={totalLength}
                                    paginationModel={paginationModel}
                                    setPaginationModel={setPaginationModel}
                                    showEntries={showEntries}
                                    setShowEntries={(size: number) => {
                                        setShowEntries(size);

                                        setPaginationModel({
                                            page: 0,
                                            size
                                        });
                                    }}
                                />
                            </div>
                        )}
                </>
            ) : (
                <div className="min-w-full h-[400px] flex justify-center items-center">
                    <div className="flex flex-col items-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary" />

                        <span className="text-sm mt-2">
                            Loading visitors...
                        </span>
                    </div>
                </div>
            )}

            {showQR && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-5">
                            <div>
                                <h2 className="text-xl font-bold">
                                    Visitor Registration
                                </h2>

                                <p className="text-xs text-gray-500 mt-1">
                                    Scan this QR code to register
                                    as a visitor.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowQR(false)}
                                className="text-gray-500 hover:text-black"
                            >
                                <RxCross1 />
                            </button>
                        </div>

                        <div
                            ref={qrRef}
                            className="flex flex-col items-center justify-center bg-white p-6"
                        >
                            <QRCode
                                value={visitorLink}
                                size={230}
                            />

                            <p className="text-xs text-gray-500 mt-3">
                                Scan to register as a visitor
                            </p>
                        </div>

                        <div className="mt-4">
                            <p className="text-xs font-semibold mb-1">
                                Visitor Link
                            </p>

                            <div className="flex gap-2">
                                <input
                                    readOnly
                                    value={visitorLink}
                                    className="border rounded px-3 py-2 text-xs flex-1 outline-none"
                                />

                                <button
                                    type="button"
                                    onClick={copyVisitorLink}
                                    className="bg-secondary text-white px-3 rounded text-xs font-bold"
                                >
                                    Copy
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-5">
                            <button
                                type="button"
                                onClick={downloadQRAsPNG}
                                className="border border-gray-300 hover:bg-gray-100 text-gray-700 py-2 rounded text-xs font-bold"
                            >
                                Download PNG
                            </button>

                            <button
                                type="button"
                                onClick={downloadQRAsPDF}
                                className="border border-gray-300 hover:bg-gray-100 text-gray-700 py-2 rounded text-xs font-bold"
                            >
                                Download PDF
                            </button>

                            <button
                                type="button"
                                onClick={printQR}
                                className="border border-gray-300 hover:bg-gray-100 text-gray-700 py-2 rounded text-xs font-bold"
                            >
                                Print
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowQR(false)}
                            className="w-full bg-secondary hover:bg-custom-blue text-white py-2 rounded mt-3 text-sm font-bold"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {showHistory && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-5 border-b">
                            <div>
                                <h2 className="text-xl font-bold">
                                    Visitor History
                                </h2>

                                <p className="text-sm text-gray-500">
                                    {historyVisitor?.name}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowHistory(false)}
                                className="text-gray-500 hover:text-black"
                            >
                                <RxCross1 />
                            </button>
                        </div>

                        <div className="p-5 overflow-y-auto max-h-[60vh]">
                            {!historyLoading ? (
                                historyData.length >
                                    0 ? (
                                    <div className="space-y-3">
                                        {historyData.map((history, index) => (
                                            <div
                                                key={history.id || index}
                                                className="border rounded-lg p-4 bg-gray-50"
                                            >
                                                <div className="flex justify-between">
                                                    <div>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {history.createdAt
                                                                ? format(
                                                                    new Date(
                                                                        history.createdAt
                                                                    ),
                                                                    "MM/dd/yyyy hh:mm a"
                                                                )
                                                                : "No date"}
                                                        </p>
                                                    </div>

                                                    {history.wellbeingRating && (
                                                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold h-fit">
                                                            {history.wellbeingRating}
                                                        </span>
                                                    )}
                                                </div>

                                                {history.prayerRequest && (
                                                    <div className="mt-3">
                                                        <p className="text-xs font-semibold text-gray-500">
                                                            Prayer Request
                                                        </p>

                                                        <p className="text-sm mt-1">
                                                            {history.prayerRequest}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                        )}
                                    </div>
                                ) : (
                                    <div className="h-40 flex items-center justify-center text-sm">
                                        No visitor history found.
                                    </div>
                                )
                            ) : (
                                <div className="h-40 flex flex-col items-center justify-center">
                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-current border-r-transparent text-secondary" />

                                    <span className="text-sm mt-2">
                                        Loading history...
                                    </span>
                                </div>
                            )}
                        </div>

                        {historyTotal >
                            0 && (
                                <div className="border-t p-4">
                                    <Pagination
                                        pageOption={[
                                            5,
                                            10,
                                            15,
                                            20
                                        ]}
                                        totalLength={historyTotal}
                                        paginationModel={historyPagination}
                                        setPaginationModel={(model: any) => {
                                            setHistoryPagination(model);

                                            if (historyVisitor) {
                                                loadHistory(
                                                    historyVisitor.id,
                                                    model.page,
                                                    model.size
                                                );
                                            }
                                        }}
                                        showEntries={historyPagination.size}
                                        setShowEntries={(size: number) => {
                                            setHistoryPagination({
                                                page: 0,
                                                size
                                            });

                                            if (historyVisitor) {
                                                loadHistory(
                                                    historyVisitor.id,
                                                    0,
                                                    size
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default Visitor;